/* eslint-disable max-len */
import React from 'react';
import './About.scss';
import { Container } from 'react-bootstrap';
import multisignalsLogo from '../../assets/img/logo/logo.png';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

// Import modern icons
import { 
  Bot, 
  TrendingUp, 
  Shield, 
  BarChart3, 
  Target, 
  CheckCircle,
  Award,
  Sparkles,
  Activity,
  Users,
  Globe,
  Cpu,
  LineChart,
  Lock,
  Clock,
  DollarSign
} from 'lucide-react';

function About() {
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('user'))?.user;
  
  const JoinUsButton = () => {
    history.push('/login');
  };
  
  const LearnButton = () => {
    history.push('/about');
  };

  // Modern Feature Card Component
  const FeatureCard = ({ icon: Icon, title, description, gradient }) => (
    <div className="modern-feature-card">
      <div className="feature-icon-wrapper" style={{ background: gradient }}>
        <Icon size={32} className="feature-icon" />
      </div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description">{description}</p>
    </div>
  );

  // Stats Component
  const StatsSection = () => (
    <div className="stats-section">
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-number">24/7</div>
          <div className="stat-label">Automated Trading</div>
        </div>
        <div className="stat-item">
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-number">$50M+</div>
          <div className="stat-label">Trading Volume</div>
        </div>
        <div className="stat-item">
          <div className="stat-icon">
            <Award size={24} />
          </div>
          <div className="stat-number">99.9%</div>
          <div className="stat-label">Uptime</div>
        </div>
        <div className="stat-item">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-number">1K+</div>
          <div className="stat-label">Active Users</div>
        </div>
      </div>
    </div>
  );

  // Service Card Component
  const ServiceCard = ({ icon: Icon, title, description, color }) => (
    <div className="service-card">
      <div className="service-icon" style={{ backgroundColor: color }}>
        <Icon size={24} />
      </div>
      <h4 className="service-title">{title}</h4>
      <p className="service-description">{description}</p>
    </div>
  );

  return (
    <div className="modern-home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <div className="hero-content">
            <div className="hero-logo-wrapper">
              <div className="logo-container">
                <div className="logo-glow"></div>
              </div>
            </div>
            
            <h1 className="hero-title">
              <span className="title-main">MultiSignals</span>
              <span className="title-subtitle">Advanced AI Trading Platform</span>
            </h1>
            
            <p className="hero-description">
              Experience the future of cryptocurrency trading with our advanced AI-powered platform. 
              Automated strategies, real-time analysis, and professional-grade tools designed for both 
              beginners and expert traders.
            </p>
            
          </div>
          
          <StatsSection />
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">
              <Sparkles size={28} className="title-icon" />
              Powerful Trading Features
            </h2>
            <p className="section-subtitle">
              Everything you need to succeed in cryptocurrency trading
            </p>
          </div>

          <div className="features-grid">
            <FeatureCard
              icon={Bot}
              title="AI-Powered Bots"
              description="Advanced trading algorithms that work 24/7 to maximize your profits with intelligent market analysis and automated execution."
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />

            <FeatureCard
              icon={Shield}
              title="Risk Management"
              description="Comprehensive risk control tools including stop-loss orders, position sizing, and portfolio diversification strategies."
              gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            />

            <FeatureCard
              icon={BarChart3}
              title="Advanced Analytics"
              description="Real-time market data, technical indicators, and detailed performance reports to make informed trading decisions."
              gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
            />

            <FeatureCard
              icon={Target}
              title="Strategy Optimization"
              description="Backtest and optimize your trading strategies using historical data to improve performance and profitability."
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            />

            <FeatureCard
              icon={Globe}
              title="Multi-Exchange"
              description="Connect to multiple cryptocurrency exchanges and manage all your trading activities from one unified platform."
              gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
            />

            <FeatureCard
              icon={Lock}
              title="Secure Trading"
              description="Bank-level security with encrypted API connections, two-factor authentication, and secure fund management."
              gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
            />
          </div>
        </Container>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">Our Trading Services</h2>
            <p className="section-subtitle">
              Comprehensive solutions for every type of trader
            </p>
          </div>

          <div className="services-grid">
            <ServiceCard
              icon={Activity}
              title="Automated Trading"
              description="Let our AI handle your trades while you focus on strategy and portfolio management."
              color="#667eea"
            />

            <ServiceCard
              icon={LineChart}
              title="Portfolio Management"
              description="Track, analyze, and optimize your cryptocurrency portfolio with advanced tools."
              color="#10b981"
            />

            <ServiceCard
              icon={Cpu}
              title="Market Analysis"
              description="Get real-time insights and predictions powered by machine learning algorithms."
              color="#f59e0b"
            />

            <ServiceCard
              icon={TrendingUp}
              title="Performance Tracking"
              description="Monitor your trading performance with detailed analytics and reporting tools."
              color="#8b5cf6"
            />
          </div>
        </Container>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <Container>
          <div className="why-choose-content">
            <div className="why-choose-logo">
              <div className="logo-container">
                <img src={multisignalsLogo} alt="Multisignals Logo" className="section-logo" />
                <div className="logo-pulse"></div>
              </div>
            </div>
            
            <h2 className="section-title">Why Choose MultiSignals?</h2>
            
            <p className="section-description">
              Join thousands of successful traders who trust MultiSignals for their cryptocurrency trading needs. 
              Our platform combines cutting-edge technology with user-friendly design to deliver exceptional results.
            </p>

            <div className="benefits-grid">
              <div className="benefit-item">
                <CheckCircle size={20} className="benefit-icon" />
                <span>Advanced AI Algorithms</span>
              </div>
              <div className="benefit-item">
                <CheckCircle size={20} className="benefit-icon" />
                <span>Real-time Market Data</span>
              </div>
              <div className="benefit-item">
                <CheckCircle size={20} className="benefit-icon" />
                <span>24/7 Automated Trading</span>
              </div>
              <div className="benefit-item">
                <CheckCircle size={20} className="benefit-icon" />
                <span>Professional Risk Management</span>
              </div>
              <div className="benefit-item">
                <CheckCircle size={20} className="benefit-icon" />
                <span>Multi-Exchange Support</span>
              </div>
              <div className="benefit-item">
                <CheckCircle size={20} className="benefit-icon" />
                <span>Expert Customer Support</span>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default About;